import { getSettings } from "./settings.js";

/**
 * Read-aloud through the browser (docs/PLAN.md §4, point 8). Quality depends
 * on the device; what we control is picking a voice that actually speaks the
 * language, and not reading too fast for a ten-year-old.
 */

/** Swiss schools teach British English and standard French. */
const PREFERRED: Record<string, string[]> = {
  en: ["en-GB", "en-IE", "en-AU", "en-US"],
  fr: ["fr-FR", "fr-CH", "fr-BE", "fr-CA"],
  de: ["de-CH", "de-DE", "de-AT"],
};

const RATE = { normal: 0.9, slow: 0.7 } as const;

function norm(tag: string): string {
  return tag.replace("_", "-").toLowerCase();
}

/**
 * The best installed voice for a language, or null when the device has none.
 * Region order first, then a voice installed on the device over a network
 * one, because the network one fails in a school WLAN that drops out.
 */
export function voiceFor(lang: string): SpeechSynthesisVoice | null {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  const regions = (PREFERRED[lang] ?? [lang]).map(norm);
  const rank = (voice: SpeechSynthesisVoice) => {
    const tag = norm(voice.lang);
    const region = regions.indexOf(tag);
    const byRegion = region === -1 ? (tag.startsWith(`${lang}-`) || tag === lang ? regions.length : 99) : region;
    return byRegion * 2 + (voice.localService ? 0 : 1);
  };
  const candidates = voices.filter((voice) => rank(voice) < 99 * 2);
  candidates.sort((a, b) => rank(a) - rank(b));
  return candidates[0] ?? null;
}

export function canSpeak(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/**
 * `onEnd` runs once the word has been read, or when reading fails or is cut
 * off by the next word: the button's «spricht» echo ends with it (#98).
 */
export function speak(text: string, lang: string, onEnd?: () => void): void {
  if (!canSpeak()) {
    onEnd?.();
    return;
  }
  // Reading aloud is an extra. Speech engines on old tablets throw in odd
  // places, and an exception here must never take a practice round down.
  try {
    window.speechSynthesis.cancel();
    // «cat|kitten»: read the first answer only, never the bar.
    const utterance = new SpeechSynthesisUtterance(text.split("|")[0]?.trim() ?? text);
    const voice = voiceFor(lang);
    utterance.lang = voice?.lang ?? PREFERRED[lang]?.[0] ?? lang;
    utterance.rate = RATE[getSettings().speechRate];
    if (onEnd) {
      utterance.onend = onEnd;
      utterance.onerror = onEnd;
    }
    try {
      if (voice) utterance.voice = voice;
    } catch {
      // The lang tag alone still picks a voice of the right language.
    }
    window.speechSynthesis.speak(utterance);
  } catch {
    // Silent: everything works without sound (docs/UX.md §1).
    onEnd?.();
  }
}

/**
 * Voices arrive asynchronously in Chrome and Safari; the list is empty on
 * first call. Call once at start so the first word already gets the right one.
 */
export function warmUpVoices(): void {
  if (!canSpeak()) return;
  window.speechSynthesis.getVoices();
  window.speechSynthesis.addEventListener?.("voiceschanged", () => window.speechSynthesis.getVoices());
}
